import * as React from "react";
import { makeStyles } from "@material-ui/styles";
import {
  Assistant,
  AvTimer,
  CheckCircleOutline,
  FiberManualRecord,
  NotInterested,
  PauseCircleOutline,
  PlayCircleOutline,
  Check,
  Warning,
  WarningTwoTone,
} from "@material-ui/icons";
import { Timeline } from "@ui/timeline";
import { fontFamilyIdealSans, fontFamilySans, weight } from "@styles/type.styles";
import { colors, greys } from "@styles/color.styles";
import { StatusEnum, ActionTypeEnum, AutopilotHistoryActionLog } from "@clockwise/schema";
import { TimelineLogType } from "@ui/timeline/timeline";
import { EventAutopilotStatusCard } from "@ui/event-autopilot-status-card";
import {
  EventAutopilotStatusCardStatus,
  shouldRenderEventAutopilotStatus,
} from "@util/event-history.util";
import { ClockwiseHistoryEmptyState } from "./clockwise-history-empty-state";
import classNames from "classnames";
import { findLastIndex } from "lodash";
import classnames from "classnames";

export interface ClockwiseHistoryProps {
  clockwiseHistoryStatus: StatusEnum;
  actions: AutopilotHistoryActionLog[];
  eventAutopilotStatus: EventAutopilotStatusCardStatus | null;
  eventOrganizerEmail: string;
  planRefreshDate: string;
  onUnpauseEvent: () => void;
  onViewPlans: () => void;
  trackTimelineExpanded: (payload: Record<string, unknown>) => void;
  hasConflict?: boolean;
  isUserInPaidPlan: boolean;
  canUserInviteOrganizerToPlan: boolean;
  onInviteOrganizerToBillingGroup: (hasPermission: boolean) => void;
}

const useStyles = makeStyles({
  headingS: {
    fontFamily: fontFamilyIdealSans,
    lineHeight: 1.5,
    fontWeight: weight.bold,
    marginBottom: 16,
    color: greys.slate,
    fontSize: `${16 / 16}rem`,
    margin: "15px 0px 8px",
  },
  defragInfoContainer: {
    backgroundColor: greys.palePaleGrey,
    padding: "7px 10px",
    margin: "10px 0px",
  },
  defragInfo: {
    margin: "3px 0px",
    fontFamily: fontFamilySans,
    lineHeight: 1.28,
    fontSize: `${12 / 16}rem`,
    color: greys.slate,
    padding: 0,
    fontWeight: 400,
  },
});

type TimelineIconByActionType = {
  [Property in ActionTypeEnum]: typeof Assistant; // This makes typing easier instead of trying to import type defined deep within mui
};

const TimelineIconByAction: TimelineIconByActionType = {
  NextAutopilotRun: AvTimer,
  EventCreated: FiberManualRecord,
  AutopilotTurnedOn: Assistant,
  AutopilotTurnedOff: NotInterested,
  ClockwiseMeetingMove: CheckCircleOutline,
  EventRescheduled: CheckCircleOutline,
  ClockwiseMovePaused: PauseCircleOutline,
  ClockwiseMoveUnpaused: PlayCircleOutline,
  ClockwiseCouldNotResolveConflict: WarningTwoTone,
};

const getTimelineIcon = (action: ActionTypeEnum, showWarning: boolean) => {
  const Icon = TimelineIconByAction[action];
  return (
    <Icon
      className={classNames({
        "cw-text-peach-darker": showWarning,
      })}
    />
  );
};

const EventAutopilotStatusIcon: Record<EventAutopilotStatusCardStatus, React.ReactNode> = {
  CanMove: <AvTimer style={{ color: colors.green.darker }} />,
  OverQuota: <PauseCircleOutline style={{ color: colors.peach.darker }} />,
  Paused: <Warning style={{ color: colors.peach.darker }} />,
  EventOutsideTimeRange: <Check style={{ color: colors.green.darker }} />,
  EventExceedsDefragRange: <AvTimer style={{ color: colors.green.darker }} />,
  Pinned: null,
  External: null,
  NoDuration: null,
  SmartHold: null,
  PrivateEvent: null,
};

const DefragInfoCard = ({ actionInfo }: { actionInfo: string[] }) => {
  const classes = useStyles();
  return (
    <div className={classes.defragInfoContainer}>
      {actionInfo.map((value) => (
        <p className={classes.defragInfo}> {value} </p>
      ))}
    </div>
  );
};

export const ClockwiseHistory = ({
  actions,
  clockwiseHistoryStatus,
  trackTimelineExpanded,
  eventAutopilotStatus,
  eventOrganizerEmail,
  planRefreshDate,
  onUnpauseEvent,
  onViewPlans,
  hasConflict,
  isUserInPaidPlan,
  canUserInviteOrganizerToPlan,
  onInviteOrganizerToBillingGroup,
}: ClockwiseHistoryProps) => {
  const classes = useStyles();

  if (!eventAutopilotStatus || clockwiseHistoryStatus === StatusEnum.AutopilotHistoryNotVisible) {
    return null;
  }

  // render an empty state if there is no AP history
  if (clockwiseHistoryStatus === StatusEnum.AutopilotHistoryOff) {
    return (
      <div>
        <h3 className={classnames(classes.headingS)} cw-id="flexible-meeting-history-section">
          History
        </h3>
        <ClockwiseHistoryEmptyState />
      </div>
    );
  }

  const timelineActions = shouldRenderEventAutopilotStatus(eventAutopilotStatus)
    ? actions
    : [...actions].filter(({ actionType }) => actionType !== ActionTypeEnum.NextAutopilotRun);

  const mostRecentInstanceOfClockwiseCouldNotResolveConflict = findLastIndex(
    timelineActions,
    (timelineAction) =>
      timelineAction.actionType == ActionTypeEnum.ClockwiseCouldNotResolveConflict,
  );

  const timelineLogs = timelineActions.map(
    ({ actionType, actionString, actionInfo, timestamp }, index) => {
      const isMostRecentCouldNotResolveConflict =
        index == mostRecentInstanceOfClockwiseCouldNotResolveConflict;
      const showWarning =
        isMostRecentCouldNotResolveConflict &&
        !!hasConflict &&
        actionType == ActionTypeEnum.ClockwiseCouldNotResolveConflict;

      const description = (
        <EventAutopilotStatusCard
          status={eventAutopilotStatus}
          isUserInPaidPlan={isUserInPaidPlan}
          eventOrganizerEmail={eventOrganizerEmail}
          canUserInviteOrganizerToPlan={canUserInviteOrganizerToPlan}
          onInviteOrganizerToBillingGroup={onInviteOrganizerToBillingGroup}
          planRefresh={planRefreshDate}
          nextDefragTimeMillis={timestamp}
          onUnpauseEvent={onUnpauseEvent}
          onViewPlans={onViewPlans}
        />
      );

      if (actionType === ActionTypeEnum.NextAutopilotRun) {
        return {
          description,
          defragInfo: null,
          timestamp: null,
          type: TimelineLogType.FutureLog,
          icon: EventAutopilotStatusIcon[eventAutopilotStatus],
        };
      } else {
        const defragInfo =
          actionInfo && actionInfo.length ? <DefragInfoCard actionInfo={actionInfo} /> : null;
        return {
          timestamp,
          defragInfo,
          description: actionString,
          type: TimelineLogType.PastLog,
          icon: getTimelineIcon(actionType, showWarning),
        };
      }
    },
  );

  return (
    <div>
      <h3 className={classnames(classes.headingS)} cw-id="flexible-meeting-history-section">
        History
      </h3>
      <Timeline
        timelineLogs={timelineLogs}
        isMissingHistory={clockwiseHistoryStatus === StatusEnum.AutopilotHistoryIncomplete}
        trackTimelineExpanded={trackTimelineExpanded}
      />
    </div>
  );
};
